import React, { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Box, CircularProgress, Typography, Chip } from '@material-ui/core';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

interface ComponentNameValidatorProps extends FieldExtensionComponentProps<string> {
  formContext?: { formData?: Record<string, any> };
}

/**
 * ComponentNameValidator - 校验自动生成的 Component 名称是否已存在
 *
 * 功能：
 * - 监听 serviceName 和 environment 字段
 * - 自动组合生成 Component 名称：{serviceName}-{environment}
 * - 实时检查组合后的 Component 名称是否已存在于 Catalog
 * - 如果已存在，显示错误提示并阻止提交
 *
 * 用法：
 * ```yaml
 * componentNameValidator:
 *   type: string
 *   ui:field: ComponentNameValidator
 *   ui:options:
 *     serviceNameField: serviceName
 *     environmentField: environment
 * ```
 */
export const ComponentNameValidator = (
  props: ComponentNameValidatorProps,
) => {
  const {
    formData,
    uiSchema,
    formContext,
  } = props;

  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [componentName, setComponentName] = useState<string>('');
  const [checking, setChecking] = useState(false);
  const [exists, setExists] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serviceNameField = uiSchema?.['ui:options']?.serviceNameField || 'serviceName';
  const environmentField = uiSchema?.['ui:options']?.environmentField || 'environment';

  // 检查 Component 是否已存在
  const checkComponentExists = async (name: string): Promise<boolean> => {
    if (!name || name.length < 2) return false;

    try {
      const response = await fetch(
        `${backendUrl}/api/catalog/entities/by-name/component/default/${name}`,
      );
      return response.ok; // 200 表示存在
    } catch (err) {
      console.error('Failed to check component existence:', err);
      return false;
    }
  };

  // 监听 serviceName 和 environment 的变化
  useEffect(() => {
    const serviceName = formContext?.formData?.[serviceNameField] || '';
    const environment = formContext?.formData?.[environmentField] || '';

    if (serviceName && environment) {
      const name = `${serviceName}-${environment}`;
      setComponentName(name);

      // 检查是否已存在
      setChecking(true);
      checkComponentExists(name).then((exists_) => {
        setExists(exists_);
        setChecking(false);
        if (exists_) {
          setError(`Component "${name}" 已存在，请使用其他服务名称或环境`);
        } else {
          setError(null);
        }
      });
    } else {
      setComponentName('');
      setExists(false);
      setError(null);
      setChecking(false);
    }
  }, [
    formContext?.formData?.[serviceNameField],
    formContext?.formData?.[environmentField],
    serviceNameField,
    environmentField,
    formContext,
    props.name,
  ]);

  // 如果没有服务名或环境，不显示任何内容
  if (!componentName) {
    return null;
  }

  return (
    <Box mt={2} mb={2}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        组件名称: <strong>{componentName}</strong>
      </Typography>
      {checking && (
        <Box display="flex" alignItems="center" mt={1}>
          <CircularProgress size={16} style={{ marginRight: 8 }} />
          <Typography variant="caption" color="textSecondary">
            检查名称是否可用...
          </Typography>
        </Box>
      )}
      {!checking && exists && (
        <Box mt={1}>
          <Chip
            label={error || `Component "${componentName}" 已存在，请使用其他服务名称或环境`}
            color="secondary"
            style={{ backgroundColor: '#f44336', color: 'white' }}
          />
        </Box>
      )}
      {!checking && !exists && componentName && (
        <Box mt={1}>
          <Chip
            label="Component 名称可用"
            style={{ backgroundColor: '#4caf50', color: 'white' }}
          />
        </Box>
      )}
    </Box>
  );
};

